import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import './WbSkuHistory.css';

function formatCurrency(value) {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
  return date.toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function buildDefaultPeriod() {
  const end = new Date();
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

function normalizeHistoryResponse(response, item) {
  const daily = Array.isArray(response?.daily) ? response.daily : [];
  const sorted = [...daily].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  return {
    sku: item?.sku || '',
    offer_id: item?.offer_id || '',
    name: item?.name || item?.sku || '',
    period_from: response?.selectedPeriod?.start || '',
    period_to: response?.selectedPeriod?.end || '',
    days: sorted.map((day) => ({
      date: day.date,
      openCount: Number(day.openCount || 0),
      cartCount: Number(day.cartCount || 0),
      orderCount: Number(day.orderCount || 0),
      orderSum: Number(day.orderSum || 0),
      buyoutCount: Number(day.buyoutCount || 0),
      buyoutPercent: Number(day.buyoutPercent || 0),
    })),
  };
}

export default function WbSkuHistory({ accountId, sku, item, onClose }) {
  const { request } = useApi();
  const defaults = useMemo(() => buildDefaultPeriod(), []);
  const [periodFrom, setPeriodFrom] = useState(defaults.start);
  const [periodTo, setPeriodTo] = useState(defaults.end);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const loadHistory = async () => {
    if (!accountId || !sku) return;
    const numericSku = Number(sku);
    if (!Number.isFinite(numericSku)) {
      setError('Некорректный SKU для загрузки истории');
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await request(`/api/v1/wb-analytics/${accountId}/products/history`, {
        method: 'POST',
        body: JSON.stringify({
          selectedPeriod: { start: periodFrom, end: periodTo },
          nmIds: [numericSku],
          skipDeletedNm: true,
          aggregationLevel: 'day',
        }),
      });
      setData(normalizeHistoryResponse(response, item));
    } catch (err) {
      setError(err?.message || 'Не удалось загрузить историю SKU');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [accountId, sku]);

  const maxOpenCount = Math.max(...((data?.days || []).map((day) => day.openCount)), 1);
  const maxOrderSum = Math.max(...((data?.days || []).map((day) => day.orderSum)), 1);

  return (
    <div className="sku-history">
      <div className="sku-history__header">
        <div>
          <h2 className="sku-history__title">{data?.name || item?.name || sku}</h2>
          <p className="sku-history__subtitle">{data?.offer_id || item?.offer_id || `SKU ${sku}`}</p>
        </div>
        {onClose ? (
          <button type="button" className="sku-history__close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        ) : null}
      </div>

      <div className="sku-history__controls">
        <div className="sku-history__date-range">
          <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
          <span>—</span>
          <input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
        </div>
        <button type="button" className="sku-history__reload" onClick={loadHistory} disabled={loading}>
          {loading ? 'Загрузка...' : 'Обновить'}
        </button>
      </div>

      {loading ? (
        <div className="wb-loading">
          <div className="wb-loading__spinner" />
          <p className="wb-loading__text">Загружаем историю SKU за последние 7 дней...</p>
        </div>
      ) : null}

      {!loading && error ? (
        <div className="wb-error">
          <p className="wb-error__title">Ошибка истории</p>
          <p className="wb-error__description">{error}</p>
        </div>
      ) : null}

      {!loading && !error && data ? (
        <>
          <div className="sku-history__charts">
            <div className="sku-history__chart">
              <h3 className="sku-history__chart-title">Открытия карточки</h3>
              <div className="sku-history__chart-bars">
                {data.days.map((day) => (
                  <div key={day.date} className="sku-history__chart-bar-container">
                    <div
                      className="sku-history__chart-bar"
                      style={{ height: `${(day.openCount / maxOpenCount) * 100}%` }}
                      title={`${day.openCount} открытий`}
                    />
                    <div className="sku-history__chart-label">{formatDate(day.date)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sku-history__chart">
              <h3 className="sku-history__chart-title">Выручка по дням</h3>
              <div className="sku-history__chart-bars">
                {data.days.map((day) => (
                  <div key={day.date} className="sku-history__chart-bar-container">
                    <div
                      className="sku-history__chart-bar"
                      style={{ height: `${(day.orderSum / maxOrderSum) * 100}%` }}
                      title={formatCurrency(day.orderSum)}
                    />
                    <div className="sku-history__chart-label">{formatDate(day.date)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="sku-history__details">
            <h3 className="sku-history__details-title">Дневная воронка</h3>
            <table className="sku-history__table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Открытия</th>
                  <th>Корзина</th>
                  <th>Заказы</th>
                  <th>Выручка</th>
                  <th>Выкуп %</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((day) => (
                  <tr key={day.date}>
                    <td className="sku-history__table-date">{formatDate(day.date)}</td>
                    <td>{day.openCount}</td>
                    <td>{day.cartCount}</td>
                    <td>{day.orderCount}</td>
                    <td>{formatCurrency(day.orderSum)}</td>
                    <td>{day.buyoutPercent.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sku-history__interpretation">
            <h3 className="sku-history__interpretation-title">Период анализа</h3>
            <div className="sku-history__interpretation-text">
              История построена за период с {formatDate(data.period_from)} по {formatDate(data.period_to)}.
              Используйте этот блок, чтобы проверить, в какой день началось падение просмотров, заказов или выручки.
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
